import { useRef, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { OverflowMenu, Edit, Settings, Upload, TrashDelete, UserAccount } from '@/assets/icons'
import { Card } from '@/components/card/Card'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { useAppDispatch } from '@/store/store-hooks'
import { useIsAccountCollaborationCapable } from '@/collaboration/hooks/useIsAccountCollaborationCapable'
import { MatterCollaborationEntrySource } from '@/analytics/enums/matter-collaboration.enums'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { useNavigate } from 'react-router-dom'
import { matterPaths } from '@/routing/paths'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import type { MatterRead } from '@/matter/store/schemas'
import { canDeleteMatter, canEditMatter, canEditMatterMembers, canEditMatterSettings } from '@/matter/utils/matter-permissions'
import { useCollaborationUpsell } from '@/collaboration/hooks/useCollaborationUpsell'
import { IconButton } from '@/components/icon-button/IconButton'

/**
 * Matter menu actions available for each matter
 */
enum MatterMenuAction {
  Edit = 'edit',
  Delete = 'delete',
  UploadFiles = 'upload-files',
  ManageMembers = 'manage-members',
  MatterSettings = 'matter-settings',
}

/**
 * Metadata for each matter menu action
 */
type MatterMenuActionConfig = {
  label: string
  value: MatterMenuAction
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  destructive?: boolean
  disabled?: boolean
}

const matterMenuActionEdit: MatterMenuActionConfig = {
  value: MatterMenuAction.Edit,
  label: 'Edit',
  icon: Edit,
}

const matterMenuActionDelete: MatterMenuActionConfig = {
  value: MatterMenuAction.Delete,
  label: 'Delete',
  icon: TrashDelete,
  destructive: true,
}

const matterMenuActionUploadFiles: MatterMenuActionConfig = {
  value: MatterMenuAction.UploadFiles,
  label: 'Upload matter files',
  icon: Upload,
}

const matterMenuActionManageMembers: MatterMenuActionConfig = {
  value: MatterMenuAction.ManageMembers,
  label: 'Manage members',
  icon: UserAccount,
}

const matterMenuActionMatterSettings: MatterMenuActionConfig = {
  value: MatterMenuAction.MatterSettings,
  label: 'Matter settings',
  icon: Settings,
}

type MatterOverflowMenuProps = {
  matter: Pick<MatterRead, 'id' | 'viewer_role' | 'accepted_at'>
  disabled?: boolean
}

/**
 * Matter Overflow Menu
 *
 * Popover based overflow menu for performing actions on a matter.
 */
export default function MatterOverflowMenu(props: MatterOverflowMenuProps) {
  const { matter, disabled = false } = props

  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const suppressFocusOnCloseRef = useRef(false)

  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const isAccountCollaborationCapable = useIsAccountCollaborationCapable()
  const { showUpsell, handleUpsellClick } = useCollaborationUpsell()

  const matterMenuActionsConfig: MatterMenuActionConfig[] = [
    ...(canEditMatter(matter) ? [matterMenuActionEdit] : []),
    matterMenuActionUploadFiles,
    ...(driveSyncEnabled && canEditMatterSettings(matter) ? [matterMenuActionMatterSettings] : []),
    ...(isAccountCollaborationCapable && canEditMatterMembers(matter) ? [matterMenuActionManageMembers] : []),
    ...(canDeleteMatter(matter) ? [matterMenuActionDelete] : []),
  ]

  const handleSelect = (action: MatterMenuAction) => {
    suppressFocusOnCloseRef.current = true
    setOpen(false)
    switch (action) {
      case MatterMenuAction.Edit:
        dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Edit, matterId: matter.id }))
        break
      case MatterMenuAction.Delete:
        dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Delete, matterId: matter.id }))
        break
      case MatterMenuAction.UploadFiles:
        navigate(matterPaths.userFiles.root(matter.id))
        break
      case MatterMenuAction.ManageMembers:
        dispatch(
          MattersActions.openDialog({ dialogType: MatterDialogType.ManageMembers, matterId: matter.id, source: MatterCollaborationEntrySource.OverflowMenu })
        )
        break
      case MatterMenuAction.MatterSettings:
        navigate(matterPaths.settings(matter.id))
        break
      default:
        break
    }
  }

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) suppressFocusOnCloseRef.current = false
        setOpen(nextOpen)
      }}
    >
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <IconButton icon={<OverflowMenu />} variant="plain" color="neutral" aria-label="Matter actions" disabled={disabled} />
      </DropdownMenu.Trigger>

      <MenuContent
        side="bottom"
        align="end"
        alignOffset={-3}
        onCloseAutoFocus={(e) => {
          if (suppressFocusOnCloseRef.current) e.preventDefault()
        }}
      >
        <MenuItemGroup>
          {matterMenuActionsConfig.map(({ value, label, icon, destructive, disabled }) => (
            <MenuItem
              key={value}
              label={label}
              icon={icon}
              variant={destructive ? 'destructive' : 'neutral'}
              disabled={disabled}
              onSelect={() => handleSelect(value)}
            />
          ))}
        </MenuItemGroup>
        {showUpsell && (
          <>
            <MenuSeparator />
            <DropdownMenu.Item
              onSelect={() => {
                suppressFocusOnCloseRef.current = true
                handleUpsellClick()
              }}
              className="max-w-[280px] cursor-pointer p-4 focus:outline-hidden"
            >
              <Card variant="upsell" size="sm" title="Add Team Members" description="Create an organization to collaborate on this matter with colleagues." />
            </DropdownMenu.Item>
          </>
        )}
      </MenuContent>
    </DropdownMenu.Root>
  )
}
